/* File: src\components\auth\WelcomeSetup.js */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../ui/AppHeader';
import LoadingScreen from '../ui/LoadingScreen';
import { useLoading } from '../../contexts/LoadingContext';

// Backend URL config
const deployedBaseUrl  = "https://us-central1-dual-n-back-web-app.cloudfunctions.net";
const emulatorBaseUrl = "http://127.0.0.1:5001/dual-n-back-web-app/us-central1";
const isLocalDev       = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const functionBaseUrl  = isLocalDev ? emulatorBaseUrl : deployedBaseUrl;
// Check if the function name is correct as deployed in your firebase.json
const checkUsernameUrl = `${functionBaseUrl}/checkUsernameAvailability`;

// Timing constants
const POLLING_INTERVAL_MS = 2000;
const MAX_WAIT_TIME_MS    = 30000; // 30 seconds
const GRACE_PERIOD_MS     = 15000; // 15 seconds
const DEBOUNCE_DELAY_MS   = 500;   // 0.5 seconds

// --- Helper Components ---
const AvailableIcon = () => <span className="text-green-500 ml-2 text-sm flex items-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>Available</span>;
const TakenIcon     = () => <span className="text-red-500 ml-2 text-sm flex items-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>Taken</span>;
const ErrorIcon     = () => <span className="text-yellow-600 ml-2 text-sm flex items-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 3.001-1.742 3.001H4.42c-1.53 0-2.493-1.667-1.743-3.001l5.58-9.92zM10 13a1 1 0 110-2 1 1 0 010 2zm-1-4a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" /></svg>Error checking</span>;

const InlineLoadingSpinner = ({ small = false }) => (
  // Removed justify-center as it will be positioned absolutely
  <div className="flex items-center">
    <svg className={`animate-spin ${small ? 'h-4 w-4' : 'h-5 w-5'} text-indigo-600`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true" >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
    </svg>
  </div>
);
// --- End Helper Components ---

/**
 * Client-side Username Validation Function
 */
const validateUsername = (uname) => {
    const trimmed = uname.trim();
    if (!trimmed) return ''; // Allow empty initially
    if (trimmed.length < 3 || trimmed.length > 25) { return "Username must be 3-25 characters long."; }
    // Allow letters, numbers, underscore, dash
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) { return "Only letters, numbers, underscores (_), and dashes (-) allowed."; }
    // Check for reserved words (optional, add more if needed)
    const reserved = ['admin', 'support', 'root', 'system', 'null', 'undefined', 'guest', 'user'];
    if (reserved.includes(trimmed.toLowerCase())) { return `Username "${trimmed}" is reserved.`; }
    return ''; // No error
};


export default function WelcomeSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    authUser,
    userSubscriptionStatus,
    isLoading: isProfileLoading, // Loading state from ProfileContext
    activeProfileId,
    addProfile, // Function to add a new profile
    markOnboardingComplete, // Function to mark onboarding complete
    error // Get error state from ProfileContext for creation errors
  } = useProfile();

  const { showLoading, hideLoading, hidePersistentLoading } = useLoading(); // Global loading indicators

  const sessionIdFromUrl = searchParams.get('session_id'); // Check for Stripe session ID
  const componentLoadTime = useRef(Date.now()); // Track component mount time
  // Unique ID for this component instance for clearer logging
  const componentInstanceId = useRef(`WelcomeSetup-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`).current;

  // State for controlling the UI flow
  const [phase, setPhase] = useState('loading'); // 'loading', 'verifying', 'username', 'error', 'timeout'
  const [message, setMessage] = useState('Initializing...'); // User-facing status message
  const [hasRedirected, setHasRedirected] = useState(false); // Flag to prevent multiple redirects

  // State for the username input form
  const [username, setUsername] = useState(''); // Current input value
  const [validationError, setValidationError] = useState(''); // Client-side format error message
  const [debouncedValue, setDebouncedValue] = useState(''); // Username value after debounce timer
  const [isChecking, setIsChecking] = useState(false); // True while fetch request for username availability is active
  const [availability, setAvailability] = useState(null); // 'available', 'taken', 'error', or null
  const [isCreating, setIsCreating] = useState(false); // True while submitting the profile creation request
  const [createError, setCreateError] = useState(null); // Error message from profile creation attempt

  // Refs for managing timers and component mount status
  const pollRef = useRef(null); // Interval timer for subscription status polling
  const timeoutRef = useRef(null); // Timeout for subscription status verification
  const debounceRef = useRef(null); // Timeout for username availability check debounce
  const isMountedRef = useRef(true); // Track if the component is still mounted

  // Cleanup timers on unmount
  const cleanup = useCallback(() => {
    clearInterval(pollRef.current);
    clearTimeout(timeoutRef.current);
    clearTimeout(debounceRef.current);
  }, []);

  // Set mount status ref and register cleanup
  useEffect(() => {
    isMountedRef.current = true;
    console.log(`[${componentInstanceId}] Component Mounted.`);
    return () => {
      isMountedRef.current = false;
      console.log(`[${componentInstanceId}] Component Unmounting.`);
      cleanup(); // Ensure timers are cleared on unmount
      hideLoading(); // Hide global loading if unmounting
      hidePersistentLoading(); // Hide persistent loading too
    };
  }, [cleanup, hideLoading, hidePersistentLoading, componentInstanceId]);


  // Hide any persistent loading indicator from previous screens on mount
  useEffect(() => {
    hidePersistentLoading();
  }, [hidePersistentLoading]);

  // --- Subscription Status Verification & Onboarding Logic ---
  useEffect(() => {
    // Prevent logic run if component unmounted or already navigated away
    if (hasRedirected || !isMountedRef.current) { cleanup(); return; }

    // Show loading screen while ProfileContext is initializing
    if (isProfileLoading) {
      if (phase !== 'loading') setPhase('loading');
      setMessage('Loading user data...');
      showLoading('Loading user data...'); // Use transient loading
      return;
    } else {
      // Hide transient loading once ProfileContext is ready
      if (phase === 'loading') hideLoading();
    }

    // If authentication check completes and there's no user, redirect to login
    if (!authUser) {
      cleanup();
      if (phase !== 'error') setPhase('error');
      setMessage('Authentication error. Redirecting to login...');
      showLoading('Redirecting...');
      setHasRedirected(true);
      // Use timeout to allow message display before navigation
      setTimeout(() => { if (isMountedRef.current) navigate('/login', { replace: true }); }, 1500);
      return;
    }

    // --- Verification Logic (Runs only if phase is loading/verifying) ---
    if (phase === 'loading' || phase === 'verifying') {
       if (phase === 'loading') setPhase('verifying'); // Ensure correct phase
       setMessage(sessionIdFromUrl ? 'Finalizing setup after checkout...' : 'Verifying account status...');

      const checkStatus = () => {
        if (!isMountedRef.current || hasRedirected || (phase !== 'verifying' && phase !== 'loading')) { cleanup(); return; }

        const status = userSubscriptionStatus; // Get latest status from context
        const elapsedMs = Date.now() - componentLoadTime.current;

        // Success conditions: Active subscription AND no profile yet -> Go to username entry
        if ((status === 'trialing' || status === 'active') && !activeProfileId) {
          console.log(`[${componentInstanceId} CheckStatus] Status OK (${status}), no profile ID. Proceeding to username phase.`);
          cleanup();
          if (isMountedRef.current) setPhase('username');
          setMessage('');
          hideLoading(); // Hide transient loading screen
          return; // Stop polling
        }
        // Success condition: Active subscription AND profile exists -> Go to dashboard
        if ((status === 'trialing' || status === 'active') && activeProfileId) {
           console.log(`[${componentInstanceId} CheckStatus] Status OK (${status}), profile ID found (${activeProfileId}). Redirecting to dashboard.`);
           cleanup();
           setMessage('Setup complete! Redirecting to dashboard…');
           showLoading('Redirecting...'); // Use transient loading for redirect
           setHasRedirected(true);
           setTimeout(() => { if (isMountedRef.current) navigate('/dashboard', { replace: true }); }, 1000);
           return; // Stop polling
        }

        // Handle problematic statuses -> Redirect to account page
        const problemStatuses = ['canceled', 'unpaid', 'error', 'incomplete_expired', 'past_due'];
        if (problemStatuses.includes(status)) {
          console.warn(`[${componentInstanceId} CheckStatus] Problematic status: ${status}. Redirecting to account.`);
          cleanup();
          if (isMountedRef.current) setPhase('error');
          setMessage(`Issue with subscription (Status: ${status}). Redirecting...`);
          showLoading('Redirecting...');
          setHasRedirected(true);
          setTimeout(() => { if (isMountedRef.current) navigate('/account', { replace: true }); }, 5000);
          return;
        }

        // Handle waiting statuses -> Continue polling, check grace period
        const waitingStatuses = ['inactive', 'no_subscription', 'loading', null, undefined];
        if (waitingStatuses.includes(status)) {
          // Check grace period ONLY if we came back from Stripe checkout
          if (sessionIdFromUrl && elapsedMs < GRACE_PERIOD_MS && (status === 'inactive' || status === 'no_subscription' || status === null || status === undefined)) {
             // Still waiting for webhook/backend update after checkout success
             console.log(`[${componentInstanceId} CheckStatus] Waiting longer (grace period)... Status: ${status || 'pending'}`);
             setMessage(`Finalizing setup... (Status: ${status || 'pending'})`);
          } else if (elapsedMs >= GRACE_PERIOD_MS && (status === 'inactive' || status === 'no_subscription')) {
             // Grace period expired, still no active subscription - likely an issue
             console.warn(`[${componentInstanceId} CheckStatus] Grace period expired, status still '${status}'. Treating as issue.`);
             cleanup();
             if (isMountedRef.current) setPhase('error');
             setMessage(`Activation seems stuck (Status: ${status}). Please check your Account page or contact support.`);
             hideLoading(); // Hide any transient loader
             return;
          } else {
             // Generic waiting message (or loading state from context)
             setMessage(`Verifying status (Current: ${status || 'loading'})...`);
          }
        }
        // Handle unexpected statuses -> Log and continue polling
        else {
             console.warn(`[${componentInstanceId} CheckStatus] Unexpected subscription status: ${status}. Continuing poll.`);
             setMessage(`Verifying status (Current: ${status})...`);
        }
      }; // end checkStatus

      // Start polling/checking
      const initialCheckTimeout = setTimeout(checkStatus, 250); // Slightly longer initial delay
      clearInterval(pollRef.current); // Clear previous interval
      pollRef.current = setInterval(checkStatus, POLLING_INTERVAL_MS);

      // Timeout Fallback for the whole verification process
      clearTimeout(timeoutRef.current); // Clear previous timeout
      timeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && !hasRedirected && (phase === 'verifying' || phase === 'loading')) {
          cleanup();
          setPhase('timeout');
          setMessage('Verification is taking longer than expected.');
          hideLoading(); // Hide transient loading
          console.warn(`[${componentInstanceId} Timeout] MAX_WAIT_TIME_MS (${MAX_WAIT_TIME_MS}ms) reached. Last Status: ${userSubscriptionStatus}`);
        }
      }, MAX_WAIT_TIME_MS);

      // Cleanup function for this effect branch
      return () => {
           clearTimeout(initialCheckTimeout);
           cleanup(); // Use shared cleanup function
      };
    }

  }, [ // Dependencies for the verification logic
    authUser, isProfileLoading, userSubscriptionStatus, activeProfileId,
    phase, hasRedirected, sessionIdFromUrl,
    navigate, cleanup, showLoading, hideLoading, componentInstanceId
  ]);


  // --- Username Input Handling ---
  const handleUsernameChange = (e) => {
    const cleanedValue = e.target.value.replace(/\s/g, ''); // Prevent spaces
    setUsername(cleanedValue);
    setValidationError(''); // Reset validation on change
    setAvailability(null); // Reset availability status
    setCreateError(null); // Reset creation error
    if (isChecking) setIsChecking(false); // Stop check if user types again
    clearTimeout(debounceRef.current); // Clear debounce timer on every change
  };


  // --- Username Availability Debounce ---
  useEffect(() => {
    const trimmed = username.trim();

    // If input is empty or doesn't meet basic criteria, don't start debounce/check
    if (!trimmed) {
      setAvailability(null);
      setValidationError('');
      setDebouncedValue(''); // Ensure debounced value is cleared
      clearTimeout(debounceRef.current); // Clear any pending timer
      return;
    }

    // Perform client-side validation immediately
    const validationMsg = validateUsername(trimmed);
    setValidationError(validationMsg); // Update validation error state

    // If format is invalid, don't proceed to debounce/check
    if (validationMsg) {
      setAvailability(null); // Reset availability if format becomes invalid
      setDebouncedValue(''); // Ensure debounced value is cleared
      clearTimeout(debounceRef.current); // Clear any pending timer
      return;
    }

    // Format is valid, set debounce timer to check availability
    clearTimeout(debounceRef.current); // Clear previous timer first
    debounceRef.current = setTimeout(() => {
      if (isMountedRef.current) {
           // Start the check process
           setIsChecking(true); // Show spinner in input
           setAvailability(null); // Clear previous availability result before check
           setDebouncedValue(trimmed.toLowerCase()); // Trigger network request effect
      }
    }, DEBOUNCE_DELAY_MS);

    // Cleanup function for this effect
    return () => clearTimeout(debounceRef.current);
  // Rerun when username input changes
  }, [username]); // Only depend on username


  // --- Username Availability Check (Network Request) ---
  useEffect(() => {
    // Run only when debouncedValue has a valid, non-empty value and component is mounted
    if (!debouncedValue || !isMountedRef.current) {
        // If debouncedValue becomes empty/null, ensure spinner stops
        if (isChecking) setIsChecking(false);
        return;
    }

    let isCancelled = false; // Flag for fetch cancellation
    // NOTE: isChecking is already set true by the debounce effect.
    // NOTE: availability is reset to null by the debounce effect.
    console.log(`[${componentInstanceId}] Checking username availability for: ${debouncedValue}`);

    fetch(checkUsernameUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: debouncedValue })
    })
      .then(async (response) => {
         if (isCancelled || !isMountedRef.current) return null;
         if (!response.ok) {
           let errorMsg = `Server error: ${response.status}`;
           try { const errorData = await response.json(); errorMsg = errorData?.error || errorMsg; } catch (jsonError) {/* ignore */}
           throw new Error(errorMsg);
         }
         return response.json();
      })
      .then(data => {
        if (!isCancelled && isMountedRef.current) {
          if (data && typeof data.available === 'boolean') {
             setAvailability(data.available ? 'available' : 'taken');
          } else {
             console.error(`[${componentInstanceId}] Invalid response format from checkUsernameAvailability:`, data);
             setAvailability('error');
          }
        }
      })
      .catch((error) => {
        if (!isCancelled && isMountedRef.current) {
           console.error(`[${componentInstanceId}] Error checking username:`, error);
           setAvailability('error');
        }
      })
      .finally(() => {
        // Always turn off the spinner, but only if the component is still mounted.
        if (!isCancelled && isMountedRef.current) {
           setIsChecking(false); // Hide spinner
        }
      });

    // Cleanup function for this effect instance
    return () => {
        isCancelled = true;
        // Optionally stop the spinner if the effect cleans up while checking
        // This handles cases where debouncedValue changes rapidly.
        if (isMountedRef.current) {
            setIsChecking(false);
        }
     };
  }, [debouncedValue, componentInstanceId]); // Only depend on the value that triggers the check and component ID.


  // --- Handle Profile Creation Submission ---
  const handleCreate = useCallback(async (e) => {
    e.preventDefault();
    // Re-validate username format and availability on submit attempt
    const currentValidationError = validateUsername(username);
    if (currentValidationError || availability !== 'available' || isCreating || isChecking) {
        setValidationError(currentValidationError || '');
        if(availability !== 'available' && !currentValidationError && !isChecking) {
            setCreateError("Username is not available or check failed. Please try a different name.");
        } else if (isChecking) {
            setCreateError("Please wait for username check to complete.");
        }
        return; // Block submission
    }
    if (!isMountedRef.current) return; // Prevent submission if unmounted

    setIsCreating(true); setCreateError(null); showLoading('Creating profile...'); // Show global loading

    try {
        const profileData = { username: username.trim() };
        const createdProfile = await addProfile(profileData);

        if (!isMountedRef.current) {
             console.warn(`[${componentInstanceId}] Component unmounted during/after addProfile call.`);
             hideLoading(); return;
         }

        if (createdProfile && createdProfile.id) {
            console.log(`[${componentInstanceId}] Profile created: ${createdProfile.id}. Marking onboarding complete and navigating...`);
            markOnboardingComplete();
            navigate('/dashboard', { replace: true });
             // hideLoading() will happen automatically on unmount via cleanup effect
        } else {
            console.error(`[${componentInstanceId}] addProfile action failed or returned unexpected value.`);
            // Use context error if available, otherwise generic message
            // Check if the context hook provides an error state we can use
            const profileContextError = error; // Assuming 'error' is exposed by useProfile()
            setCreateError(profileContextError || "Failed to create profile. Username might be taken or error occurred.");
            hideLoading(); setIsCreating(false); setAvailability(null); // Reset availability
        }
    } catch (caughtError) {
        console.error(`[${componentInstanceId}] Error THROWN during addProfile call:`, caughtError);
        if (isMountedRef.current) {
           let displayMessage = "An unexpected error occurred during profile creation.";
            // Example: Customize message based on caught error type if needed
           if (caughtError.message?.toLowerCase().includes('permission-denied') || caughtError.message?.toLowerCase().includes('username already taken')) {
                displayMessage = "This username is already taken. Please choose another.";
                setAvailability('taken'); // Force availability state to taken
           } else if (caughtError instanceof Error) {
             displayMessage = caughtError.message;
           }
           setCreateError(displayMessage); hideLoading(); setIsCreating(false); setAvailability(null);
        }
   }
  }, [
      username, availability, isCreating, isChecking, addProfile,
      showLoading, hideLoading, navigate, componentInstanceId,
      markOnboardingComplete, error // Added context 'error' dependency
  ]);


  // --- Render Logic ---
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <AppHeader />
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">

          {/* === Username Input Phase === */}
          {phase === 'username' ? (
            <div className="bg-white p-8 rounded-lg shadow-lg animate-fadeIn">
              <h1 className="text-center text-2xl font-extrabold text-gray-900 mb-2"> Choose Your Username </h1>
              <p className="text-center text-sm text-gray-600 mb-6"> Visible to others. Cannot be changed later. </p>
              <form onSubmit={handleCreate} className="space-y-4" noValidate>
                <div>
                  <label htmlFor="username" className="sr-only">Username</label>
                  {/* Wrapper div for relative positioning of spinner */}
                  <div className="relative">
                    <input
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="off" // Prevent browser autocomplete interference
                      spellCheck="false" // Disable spellcheck
                      required
                      maxLength={25}
                      value={username}
                      onChange={handleUsernameChange}
                      placeholder="e.g. BrainMaster_101 (no spaces)"
                      disabled={isCreating} // Disable only when creation call is active
                      className={`appearance-none rounded-md relative block w-full px-3 py-2 border placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm transition-colors duration-150 ${
                          validationError ? 'border-red-400 ring-1 ring-red-400' // Client format error
                          : !isChecking && (availability === 'taken' || availability === 'error') ? 'border-red-400 ring-1 ring-red-400' // Server unavailable/error (only show border AFTER check)
                          : !isChecking && availability === 'available' ? 'border-green-400 ring-1 ring-green-400' // Server available (only show border AFTER check)
                          : 'border-gray-300' // Default state or while checking
                      } ${isCreating ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`} // Visual feedback for disabled
                      aria-invalid={!!validationError || (!isChecking && (availability === 'taken' || availability === 'error'))}
                      aria-describedby="username-feedback"
                    />
                    {/* Spinner inside input, shown only when checking availability */}
                    {isChecking && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" aria-label="Checking username availability...">
                        <InlineLoadingSpinner small />
                      </div>
                    )}
                  </div>
                  {/* Feedback Area: Shows format error OR availability status (ONLY WHEN NOT CHECKING) */}
                  <div id="username-feedback" className="mt-2 text-sm min-h-[20px] flex items-center">
                    {/* Show validation error first if present (regardless of checking) */}
                    {validationError ? (
                       <span className="text-red-600 ml-2 text-sm flex items-center"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>{validationError}</span>
                    /* Only show availability status if NO validation error AND check is NOT in progress */
                    ) : !isChecking && !validationError && availability === 'available' ? (
                        <AvailableIcon />
                    ) : !isChecking && !validationError && availability === 'taken' ? (
                        <TakenIcon />
                    ) : !isChecking && !validationError && availability === 'error' ? (
                        <ErrorIcon />
                    /* Optionally show a placeholder or nothing */
                    ) : null}
                  </div>
                  {/* Area for errors specifically from the creation process */}
                  {createError && <p className="mt-2 text-sm text-red-600" role="alert">{createError}</p>}
                </div>

                {/* Submit Button: Disabled if creating, checking, format error, not available, or empty */}
                <button
                  type="submit"
                  disabled={ isCreating || isChecking || !!validationError || availability !== 'available' || !username.trim() }
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150" >
                  {isCreating ? ( <><InlineLoadingSpinner small /> <span className="ml-2">Creating Profile...</span></> ) : 'Create Profile & Continue'}
                </button>
              </form>
            </div>

          /* === Loading / Verifying / Error / Timeout Phases === */
          ) : (
            <div className="text-center">
              {/* Show loading screen unless it's the username phase */}
              {phase !== 'username' && ( <LoadingScreen message={message} bgColor="bg-transparent" fullScreen={false} className="mb-6" /> )}
              {/* Show error/timeout message box */}
              {(phase === 'error' || phase === 'timeout') && !hasRedirected && (
                    <div className="mt-6 bg-white p-4 rounded-lg shadow-md max-w-md mx-auto animate-fadeIn">
                       <p className="text-sm text-gray-700">{message}</p>
                       {(phase === 'timeout' || phase === 'error') && (
                         <p className="mt-3 text-sm"> If the problem persists, please visit your{' '} <a href="/account" className="font-medium text-indigo-600 hover:text-indigo-500 underline"> Account Page </a> or contact support. </p>
                       )}
                    </div>
                )}
            </div>
          )}
        </div>
      </div>
      {/* Embedded simple animation styles */}
      <style jsx global>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
}